import React, { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Sparkles, Calendar } from 'lucide-react'
import {
  ChangeGroups,
  VersionTag,
  formatReleaseDate,
} from '../components/whatsNew/changeTypes'
import WhatsNewPopup from '../components/WhatsNewPopup'
import { PLACEHOLDER_RELEASES } from '../components/whatsNew/placeholderReleases'

/* -------------------------------------------------------------------------- */
/* Release card                                                               */
/* -------------------------------------------------------------------------- */

/**
 * One release, always open. No chevron, no change count, no toggle — the whole
 * history is a single scroll, so nothing is hidden behind a click.
 */
function ReleaseCard({ release, isLatest, index }) {
  return (
    <article
      aria-label={`Release ${release.version}`}
      className="animate-card-in overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
      style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
    >
      <div className="flex flex-wrap items-center gap-2 px-5 py-4">
        <VersionTag version={release.version} highlight={isLatest} />
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Calendar className="h-[13px] w-[13px]" strokeWidth={2} aria-hidden="true" />
          <time dateTime={release.date}>{formatReleaseDate(release.date)}</time>
        </span>
      </div>

      <div className="border-t border-gray-100 px-5 pb-[18px] pt-4 dark:border-gray-800">
        <ChangeGroups changes={release.changes} stagger={30} />
      </div>
    </article>
  )
}

/* -------------------------------------------------------------------------- */
/* Loading + empty                                                            */
/* -------------------------------------------------------------------------- */

function ReleaseSkeleton({ lines = 3, index = 0 }) {
  return (
    <div
      className="animate-card-in overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="flex items-center gap-2 px-5 py-4">
        <div className="skeleton h-5 w-[58px]" />
        <div className="skeleton h-3 w-[108px]" />
      </div>
      <div className="flex flex-col gap-2.5 border-t border-gray-100 px-5 pb-[18px] pt-4 dark:border-gray-800">
        {Array.from({ length: lines }).map((_, i) => (
          <div key={i} className="skeleton h-3" style={{ width: `${88 - i * 11}%` }} />
        ))}
      </div>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="animate-card-in flex flex-col items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-6 py-11 shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-700/30 dark:text-primary-300">
        <Sparkles className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
      </div>
      <p className="m-0 text-[15px] font-semibold text-gray-900 dark:text-gray-100">
        No release notes yet
      </p>
      <p
        className="m-0 max-w-[400px] text-center text-sm leading-5 text-gray-500 dark:text-gray-400"
        style={{ textWrap: 'pretty' }}
      >
        Updates will appear here as soon as the first release is published. You will
        also see a one-time notice when you next sign in.
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function WhatsNew() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  /* ────────────────────────────────────────────────────────────────────────
   * API WIRING GOES HERE.
   *
   * Swap the three lines below for the real fetch — the page already renders
   * every state off them:
   *   loading === true   → skeleton cards
   *   releases === []    → the empty state
   *   releases.length    → the list, newest first (the page does not re-sort)
   * ──────────────────────────────────────────────────────────────────────── */
  const [releases] = useState(PLACEHOLDER_RELEASES)
  const [loading] = useState(false)

  /*
   * Design-review only: /whats-new?popup=1 renders the one-time sign-in popup
   * over the page so it can be looked at without a real "unseen release" check.
   * Delete this once the popup is triggered for real after sign-in.
   */
  const previewPopup = searchParams.get('popup') === '1'
  const closePreview = () => {
    searchParams.delete('popup')
    setSearchParams(searchParams, { replace: true })
  }

  return (
    <div className="animate-page-in flex max-w-[920px] flex-col gap-5">
      <header className="animate-header-in flex items-center gap-3.5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm">
          <Sparkles className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h1 className="m-0 text-2xl font-bold leading-[30px] tracking-tight text-gray-900 dark:text-gray-100">
            What's New
          </h1>
          <p
            className="m-0 mt-1 text-sm text-gray-500 dark:text-gray-400"
            style={{ textWrap: 'pretty' }}
          >
            See what's changed in Xtract, release by release.
          </p>
        </div>
      </header>

      {loading ? (
        <div className="flex flex-col gap-3">
          <ReleaseSkeleton lines={3} index={0} />
          <ReleaseSkeleton lines={2} index={1} />
        </div>
      ) : releases.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-3">
          {releases.map((release, i) => (
            <ReleaseCard
              key={release.version}
              release={release}
              isLatest={i === 0}
              index={i}
            />
          ))}
        </div>
      )}

      {previewPopup && releases.length > 0 && (
        <WhatsNewPopup
          release={releases[0]}
          onDismiss={closePreview}
          onViewChangelog={() => {
            closePreview()
            navigate('/whats-new')
          }}
        />
      )}
    </div>
  )
}
